var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CombinationMatcherConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import './combinationMatcherMatchColumns'; // force import for JsonSchema
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { getRenamedColumns } from './toolConfigUtil';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
export var CombinationMatcherInput;
(function (CombinationMatcherInput) {
    CombinationMatcherInput["top"] = "top";
    CombinationMatcherInput["bottom"] = "bottom";
})(CombinationMatcherInput || (CombinationMatcherInput = {}));
export var CombinationMatcherDefaultColumnName;
(function (CombinationMatcherDefaultColumnName) {
    CombinationMatcherDefaultColumnName["Matched"] = "Matched";
    CombinationMatcherDefaultColumnName["MatchID"] = "MatchID";
})(CombinationMatcherDefaultColumnName || (CombinationMatcherDefaultColumnName = {}));
const schema = {
    $id: 'urn:wisepipe-common-pipe:CombinationMatcherConfig',
    type: 'object',
    properties: {
        matchColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:CombinationMatcherMatchColumns' },
            minItems: 1,
            uniqueItemProperties: ['combination', 'data']
        },
        combinationInput: {
            type: 'string',
            enum: Object.values(CombinationMatcherInput)
        },
        idColumn: { type: 'string' },
        matchedAlias: { type: 'string' },
        matchIDAlias: { type: 'string' }
    },
    required: ['matchColumns', 'combinationInput']
};
let CombinationMatcherConfig = CombinationMatcherConfig_1 = class CombinationMatcherConfig extends ToolConfig {
    constructor(matchColumns, combinationInput, idColumn, matchedAlias, matchIDAlias, customFormats) {
        super(ToolType.COMBINATION_MATCHER, customFormats);
        this.matchColumns = matchColumns;
        this.combinationInput = combinationInput;
        this.idColumn = idColumn;
        this.matchedAlias = matchedAlias === '' ? undefined : matchedAlias;
        this.matchIDAlias = matchIDAlias === '' ? undefined : matchIDAlias;
        JSON.validate(this, CombinationMatcherConfig_1);
    }
    get combination() {
        if (this.combinationInput === CombinationMatcherInput.top) {
            return CombinationMatcherConfig_1.top;
        }
        return CombinationMatcherConfig_1.bottom;
    }
    get data() {
        if (this.combinationInput === CombinationMatcherInput.bottom) {
            return CombinationMatcherConfig_1.top;
        }
        return CombinationMatcherConfig_1.bottom;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return CombinationMatcherConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return CombinationMatcherConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return CombinationMatcherConfig_1.outs;
    }
    static get top() {
        return CombinationMatcherConfig_1.ins[0];
    }
    static get bottom() {
        return CombinationMatcherConfig_1.ins[1];
    }
    static get out() {
        return CombinationMatcherConfig_1.outs[0];
    }
    static get ins() {
        return ['top', 'bottom'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.COMBINATION_MATCHER.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.COMBINE], this.ins, this.outs, ['match', 'combination', 'merge', 'combine'], false);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        const combination = ins.schemas.at(this.combination);
        const data = ins.schemas.at(this.data);
        if (!combination || !data) {
            return tsb.build();
        }
        exp.copyAll(this.data);
        const combinationColsForMatching = [];
        const dataColsForMatching = [];
        this.matchColumns.forEach((cols) => {
            combinationColsForMatching.push(cols.combination);
            dataColsForMatching.push(cols.data);
        });
        const combinationColumnPool = combination.reduce((set, c) => set.add(c.name), new Set());
        const dataColumnPool = data.reduce((set, c) => set.add(c.name), new Set());
        const combinedColumns = [...combinationColumnPool, ...dataColumnPool];
        const combinationDependents = this.getDependents(exp, this.combination, combinationColsForMatching);
        const dataDependents = this.getDependents(exp, this.data, dataColsForMatching);
        const dependents = combinationDependents.concat(dataDependents);
        const newColumnFormats = new Map();
        const uniqueMatchedAlias = getRenamedColumns(combinedColumns, this.matchedAlias ? this.matchedAlias : CombinationMatcherDefaultColumnName.Matched, this.toolType);
        if (combination && this.idColumn) {
            const uniqueMatchedIDAlias = getRenamedColumns(combinedColumns, this.matchIDAlias ? this.matchIDAlias : CombinationMatcherDefaultColumnName.MatchID, this.toolType);
            const idColumn = combination.find((colMet) => colMet.name === this.idColumn);
            dependents.push(idColumn);
            exp.makeNew(new Column(uniqueMatchedAlias, 'boolean'), dependents, true);
            exp.makeNew(new Column(uniqueMatchedIDAlias, idColumn.type), dependents, true);
            if (idColumn?.customFormat) {
                newColumnFormats.set(uniqueMatchedIDAlias, idColumn.customFormat.copyWithAutoGeneratedTrue());
            }
        }
        else {
            exp.makeNew(new Column(uniqueMatchedAlias, 'boolean'), dependents, true);
        }
        exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const combination = scope.schemas.at(this.combination);
        const data = scope.schemas.at(this.data);
        if (!combination || !data) {
            return ToolValidationResult.getPortMissingEdgesResult('The tool is missing one or more input nodes.', 'Combination matcher requires two input nodes.');
        }
        const combinationColumnPool = combination.reduce((set, c) => set.add(c.name), new Set());
        const dataColumnPool = data.reduce((set, c) => set.add(c.name), new Set());
        // ensure data doesn't have name conflict with output columns
        const outputs = [
            getRenamedColumns([...combinationColumnPool, ...dataColumnPool], this.matchedAlias ? this.matchedAlias : CombinationMatcherDefaultColumnName.Matched, this.toolType),
            getRenamedColumns([...combinationColumnPool, ...dataColumnPool], this.matchIDAlias ? this.matchIDAlias : CombinationMatcherDefaultColumnName.MatchID, this.toolType)
        ];
        for (const output of outputs) {
            if (dataColumnPool.has(output))
                return ToolValidationResult.getBadMetadataResult(`The output column name "${output}" conflicts with an existing column in the data.`);
        }
        // check if the inputs contain the columns used for matching
        for (const cols of this.matchColumns) {
            if (!combinationColumnPool.has(cols.combination)) {
                return ToolValidationResult.getBadMetadataResult(`The column "${cols.combination}" is not found in the combination source.`);
            }
            if (!dataColumnPool.has(cols.data)) {
                return ToolValidationResult.getBadMetadataResult(`The column "${cols.data}" is not found in the data source.`);
            }
        }
        if (this.idColumn && !combinationColumnPool.has(this.idColumn)) {
            return ToolValidationResult.getBadMetadataResult(`The column "${this.idColumn}" is not found in the combination source.`);
        }
        return ToolValidationResult.getValidResult();
    }
};
CombinationMatcherConfig = CombinationMatcherConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.COMBINATION_MATCHER)
], CombinationMatcherConfig);
export { CombinationMatcherConfig };
//# sourceMappingURL=combinationMatcherConfig.js.map